/**
 * RateLimitInsights — per management-API-key usage-vs-limit chart.
 *
 * Fetches GET /api/mgmt/v1/admin/rate-limit/keys (scope: api_keys:read) using
 * the Bearer key pasted in the Developer Portal auth strip, and renders, for
 * each key, a 24-hour sparkline of requests/min against the key's limit so
 * partners can see how close each key runs to its ceiling.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Gauge, RefreshCw, Loader2, AlertTriangle } from 'lucide-react';

function Sparkline({ series, limit }) {
  const counts = series.map((s) => s.count);
  const peak = Math.max(limit || 1, ...counts, 1);
  return (
    <div className="flex items-end gap-[2px] h-10" data-testid="rate-limit-sparkline">
      {series.map((s, i) => {
        const pct = Math.max(2, Math.round((s.count / peak) * 100));
        const over = limit && s.count >= limit;
        return (
          <div
            key={i}
            title={`${s.hour} · ${s.count} req`}
            className={`flex-1 rounded-sm ${over ? 'bg-rose-400' : 'bg-primary/70'}`}
            style={{ height: `${pct}%` }}
          />
        );
      })}
    </div>
  );
}

export default function RateLimitInsights({ apiBase, bearerKey }) {
  const [expanded, setExpanded] = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);
  const [data,     setData]     = useState([]);

  const load = useCallback(async () => {
    if (!bearerKey) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${apiBase}/api/mgmt/v1/admin/rate-limit/keys`, {
        headers: { 'Authorization': `Bearer ${bearerKey}` },
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(b?.detail?.message || b?.detail || `HTTP ${res.status}`);
      }
      const body = await res.json();
      setData(Array.isArray(body.data) ? body.data : []);
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  }, [apiBase, bearerKey]);

  useEffect(() => { if (expanded && bearerKey) load(); }, [expanded, bearerKey, load]);

  return (
    <div className="rounded-lg border border-border/40 bg-muted/10 mb-6" data-testid="developers-rate-limit-insights">
      <button
        type="button"
        onClick={() => setExpanded((x) => !x)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
        data-testid="developers-rate-limit-toggle"
      >
        <div className="flex items-center gap-2">
          <Gauge className="w-4 h-4 text-primary" />
          <span className="text-sm font-medium">Rate-Limit Insights</span>
          <span className="text-[10px] text-muted-foreground">per-key usage over the last 24h</span>
        </div>
        <span className="text-[10px] text-muted-foreground">{expanded ? 'Hide' : 'Show'}</span>
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-border/40 pt-3">
          {!bearerKey && (
            <div className="text-[11px] text-amber-300 flex items-center gap-1.5">
              <AlertTriangle className="w-3 h-3" /> Paste an API key above (scope <code className="text-[10px] mx-1 px-1 rounded bg-muted/40">api_keys:read</code>) to load usage.
            </div>
          )}

          {bearerKey && (
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-muted-foreground">Bars turn red on minutes that hit the limit.</span>
              <button
                type="button"
                onClick={load}
                disabled={loading}
                className="text-[10px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1 disabled:opacity-40"
                data-testid="developers-rate-limit-refresh"
              >
                {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Refresh
              </button>
            </div>
          )}

          {error && (
            <div className="text-[11px] text-rose-300 border border-rose-500/30 rounded px-2 py-1.5" data-testid="developers-rate-limit-error">
              {error}
            </div>
          )}

          {bearerKey && !loading && !error && data.length === 0 && (
            <div className="text-[11px] text-muted-foreground italic px-3 py-6 text-center" data-testid="developers-rate-limit-empty">
              No active management keys with recorded usage yet.
            </div>
          )}

          <div className="space-y-2" data-testid="developers-rate-limit-list">
            {data.map((k) => (
              <div
                key={k.key_id}
                className="rounded border border-border/40 bg-background/40 px-3 py-2"
                data-testid={`rate-limit-key-${k.key_id}`}
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-xs font-medium truncate">{k.name || k.key_id}</span>
                  <span className={`text-[9px] px-1.5 py-0.5 rounded border ${k.env === 'test' ? 'border-amber-500/40 text-amber-300 bg-amber-500/10' : 'border-emerald-500/40 text-emerald-300 bg-emerald-500/10'}`}>
                    {k.env}
                  </span>
                  {k.prefix ? <span className="text-[10px] font-mono text-muted-foreground">{k.prefix}…</span> : null}
                  <span className="ml-auto text-[10px] text-muted-foreground">
                    {k.requests_24h} req / 24h · limit {k.limit_per_min}/min
                  </span>
                </div>
                <Sparkline series={k.series || []} limit={k.limit_per_min} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
